import Button from '@mui/material/Button';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'react-i18next';
import { Article } from '@/types/travelguide';
import {
  HomeAdventureContentGrid,
  HomeAdventureHighlight,
  HomeAdventureHtml,
  HomeAdventureHighlightHtml,
  HomeAdventureSection,
  HomeAdventureTitle,
  HomeAdventureTourGrid,
  HomeEtaxiAction,
  HomeEtaxiContentColumn,
  HomeEtaxiHtml,
  HomeEtaxiImageColumn,
  HomeEtaxiImageWrap,
  HomeEtaxiLayout,
  HomeEtaxiSection,
  HomeEtaxiTitle,
  HomeExperienceSection,
  HeroContent,
  HeroOverlay,
  HeroTitle,
  HeroVideo,
  HeroVideoWrap,
  HeroWave,
  HomeContainer,
  HomeHeroSection,
  HomeServiceDescription,
  HomeServiceSection,
  HomeServiceTitle,
  HomeTravelGuideAction,
  HomeTravelGuideBody,
  HomeTravelGuideCard,
  HomeTravelGuideCardTitle,
  HomeTravelGuideDate,
  HomeTravelGuideDescription,
  HomeTravelGuideExcerpt,
  HomeTravelGuideGrid,
  HomeTravelGuideHeader,
  HomeTravelGuideSection,
  HomeTravelGuideThumbnailWrap,
  HomeTravelGuideTitle,
} from './style';
import TourCard from './TourCard';
import { getImageUrl } from '@/utils/utils';

type HomePageProps = {
  content: any;
  tours?: any[];
  overlayHeader?: boolean;
};

const TravelArticleCard = ({
  article,
  lang,
}: {
  article: Article;
  lang: string;
}) => {
  const slugPath = article?.slug || encodeURIComponent(article?.title || '');
  const articleHref = `/${lang}/hoiantravelguide/${slugPath}`;

  return (
    <HomeTravelGuideCard>
      <HomeTravelGuideThumbnailWrap>
        <Link href={articleHref} hrefLang={lang}>
          <Image
            src={article?.thumbnail || '/assets/images/logo.png'}
            alt={article?.title || 'Hoi An Travel Guide'}
            fill
            sizes="(max-width: 768px) 100vw, 33vw"
            style={{ objectFit: 'cover' }}
          />
        </Link>
      </HomeTravelGuideThumbnailWrap>
      <HomeTravelGuideBody>
        <Link
          href={articleHref}
          hrefLang={lang}
          style={{ textDecoration: 'none' }}
        >
          <HomeTravelGuideCardTitle>
            {article?.title || ''}
          </HomeTravelGuideCardTitle>
        </Link>
        <HomeTravelGuideDate>
          {article?.date_created
            ? new Date(article.date_created).toDateString()
            : ''}
        </HomeTravelGuideDate>
        <HomeTravelGuideExcerpt>
          {article?.excerpt || ''}
        </HomeTravelGuideExcerpt>
      </HomeTravelGuideBody>
    </HomeTravelGuideCard>
  );
};

const HomePage = ({
  content: _content,
  tours = [],
  overlayHeader = false,
}: HomePageProps) => {
  const router = useRouter();
  const { t } = useTranslation('common');
  const lang = router.locale ?? 'en';
  const serviceTitle = _content?.service_title ?? _content?.service_titl ?? '';
  const serviceDescription = _content?.service_description ?? '';
  const adventureTitle = _content?.adventure_title ?? 'Adventure Tours';
  const leftContentHtml = _content?.adventure_left_content ?? '';
  const rightContentHtml = _content?.adventure_right_content ?? '';
  const experienceTitle = _content?.experience_title ?? 'Experience Tours';
  const experienceLeftContentHtml = _content?.experience_left_content ?? '';
  const experienceRightContentHtml = _content?.experience_right_content ?? '';
  const adventureTours = _content?.adventure_tours;
  const experienceTours = _content?.experience_tours;
  const eTaxi = _content?.etaxi;
  const travelArticles = Array.isArray(_content?.travel_articles)
    ? _content.travel_articles
    : [];
  return (
    <>
      <HomeHeroSection overlayHeader={overlayHeader}>
        <HeroVideoWrap>
          <HeroVideo
            src="/medias/HoiAnGoWebsitev3-7Qapp.mp4"
            autoPlay
            loop
            muted
            playsInline
            aria-label="Hoi An Go website background video"
          />
          <HeroOverlay />
        </HeroVideoWrap>

        <HeroContent>
          <HeroTitle>
            HoiAnGO
            <br />
            {t('homeHeroTitle')}
          </HeroTitle>
        </HeroContent>

        <HeroWave />
      </HomeHeroSection>

      <HomeServiceSection
        role="region"
        aria-labelledby="hoiango-services-title"
      >
        <HomeContainer>
          <HomeServiceTitle id="hoiango-services-title">
            {serviceTitle}
          </HomeServiceTitle>
          <HomeServiceDescription>{serviceDescription}</HomeServiceDescription>
        </HomeContainer>
      </HomeServiceSection>

      <HomeAdventureSection>
        <HomeContainer>
          <HomeAdventureTitle>{adventureTitle}</HomeAdventureTitle>
          <HomeAdventureContentGrid>
            <HomeAdventureHtml
              dangerouslySetInnerHTML={{ __html: leftContentHtml }}
            />
            <HomeAdventureHighlight>
              <HomeAdventureHighlightHtml
                dangerouslySetInnerHTML={{ __html: rightContentHtml }}
              />
            </HomeAdventureHighlight>
          </HomeAdventureContentGrid>

          <HomeAdventureTourGrid>
            {adventureTours.map((tour: any) => {
              return <TourCard key={tour.id} tour={tour} lang={lang} />;
            })}
          </HomeAdventureTourGrid>
        </HomeContainer>
      </HomeAdventureSection>

      <HomeExperienceSection>
        <HomeContainer>
          <HomeAdventureTitle>{experienceTitle}</HomeAdventureTitle>
          <HomeAdventureContentGrid>
            <HomeAdventureHtml
              dangerouslySetInnerHTML={{ __html: experienceLeftContentHtml }}
            />
            <HomeAdventureHighlight>
              <HomeAdventureHighlightHtml
                dangerouslySetInnerHTML={{ __html: experienceRightContentHtml }}
              />
            </HomeAdventureHighlight>
          </HomeAdventureContentGrid>

          <HomeAdventureTourGrid>
            {experienceTours.map((tour: any) => {
              return (
                <TourCard
                  key={tour.id}
                  tour={tour}
                  lang={lang}
                  buttonBgColor="#002E5B"
                  buttonHoverBgColor="#01264A"
                />
              );
            })}
          </HomeAdventureTourGrid>
        </HomeContainer>
      </HomeExperienceSection>

      <HomeEtaxiSection>
        <HomeEtaxiLayout>
          <HomeEtaxiImageColumn>
            <HomeEtaxiImageWrap>
              <Image
                src={
                  getImageUrl(eTaxi?.image || '') || '/assets/images/logo.png'
                }
                alt="HoiAnGO E-Taxi with passengers"
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                priority
                style={{ objectFit: 'cover' }}
              />
            </HomeEtaxiImageWrap>
          </HomeEtaxiImageColumn>

          <HomeEtaxiContentColumn>
            <HomeEtaxiTitle>
              <Trans>HoiAnGO E-Taxi</Trans>
            </HomeEtaxiTitle>
            {eTaxi?.content ? (
              <HomeEtaxiHtml
                dangerouslySetInnerHTML={{ __html: eTaxi.content }}
              />
            ) : null}
            <HomeEtaxiAction>
              <Link
                href={`/?tab=e_taxi`}
                hrefLang={lang}
                tabIndex={0}
                aria-label="Book E-Taxi"
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#002E5B',
                    color: '#FFFFFF',
                    px: 3,
                    py: 1.25,
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: '#01264A',
                    },
                  }}
                >
                  <LocalTaxiOutlinedIcon sx={{ mr: 1 }} />
                  <Trans i18nKey="home.bookETaxi" defaults="BOOK E-TAXI" />
                </Button>
              </Link>
            </HomeEtaxiAction>
          </HomeEtaxiContentColumn>
        </HomeEtaxiLayout>
      </HomeEtaxiSection>

      <HomeTravelGuideSection>
        <HomeContainer>
          <HomeTravelGuideHeader>
            <HomeTravelGuideTitle>
              <Trans i18nKey="home.travelGuideTitle" defaults="HoiAnGO Travel Guide" />
            </HomeTravelGuideTitle>
            <HomeTravelGuideDescription>
              <Trans i18nKey="home.travelGuideDescription" defaults="Let us take you to the best spots and show you the top things to do in Hoi An." />
            </HomeTravelGuideDescription>
          </HomeTravelGuideHeader>

          <HomeTravelGuideGrid>
            {travelArticles.map((item: Article, index: number) => (
              <TravelArticleCard
                key={item?.id ?? index}
                article={item}
                lang={lang}
              />
            ))}
          </HomeTravelGuideGrid>

          <HomeTravelGuideAction>
            <Link
              href={`/${lang}/hoiantravelguide`}
              hrefLang={lang}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#002E5B',
                  color: '#FFFFFF',
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: '#012F5B',
                  },
                }}
              >
                <Trans i18nKey="home.viewAllTravelGuides" defaults="View All Travel Guides" />
              </Button>
            </Link>
          </HomeTravelGuideAction>
        </HomeContainer>
      </HomeTravelGuideSection>
    </>
  );
};

export default HomePage;
